import React from "react";
import { useSettings } from "../lib/SettingsContext";
import { API } from "../lib/api";
import { Phone, Mail, Clock, MessageCircle, MapPin } from "lucide-react";

const imgSrc = (p) => {
  if (!p) return "";
  if (p.startsWith("http") || p.startsWith("data:")) return p;
  return `${API}/files/${p}`;
};

export default function ContactPage() {
  const { settings } = useSettings();

  const whatsappRaw = (settings.company_whatsapp || "").replace(/\D/g, "");
  const waLink = whatsappRaw ? `https://wa.me/${whatsappRaw}` : null;
  const contactImage = imgSrc(settings.contact_image_url);
  const contactImage2 = imgSrc(settings.contact_image_url_2);
  // Build a readable full address: prefer the explicit multiline field, else compose from single-line + city.
  const composedAddress = [settings.company_address, settings.company_city].filter(Boolean).join(", ");
  const fullAddress = (settings.company_address_full || "").trim() || composedAddress;
  const mapsHref = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="contact-page">
      <div className="text-xs font-mono uppercase text-neutral-500">/contacto</div>
      <h1 className="font-display text-5xl md:text-7xl tracking-tighter mt-1">Hablemos.</h1>
      <p className="text-neutral-600 mt-3 max-w-xl">
        Estamos aquí para asesorarte con tu proyecto de impresión personalizada. Cotizamos, produimos y enviamos a todo México.
      </p>

      {/* Prominent WhatsApp CTA — top of page, always visible */}
      {waLink && (
        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          className="mt-8 p-5 md:p-6 flex items-center gap-4 bg-emerald-500 text-white hover:bg-emerald-600 transition-colors max-w-2xl border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-0.5"
          data-testid="contact-whatsapp-cta"
        >
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-7 h-7 md:w-8 md:h-8" />
          </div>
          <div className="flex-1">
            <div className="font-display text-2xl md:text-3xl tracking-tight">Chatea por WhatsApp</div>
            <div className="text-sm opacity-90 mt-0.5">Respuesta en menos de 1 hora · {settings.company_whatsapp}</div>
          </div>
          <div className="hidden md:block text-xs font-mono uppercase bg-black/20 px-3 py-1.5">Abrir chat →</div>
        </a>
      )}

      <div className="grid lg:grid-cols-5 gap-6 mt-10">
        <div className="lg:col-span-2 space-y-4">
          <div className="brutal-card p-5" data-testid="contact-info-block">
            <div className="text-xs font-mono uppercase text-neutral-500">Empresa</div>
            <div className="font-display text-2xl tracking-tight mt-1">{settings.company_name}</div>

            <div className="mt-5 space-y-3 text-sm">
              {settings.company_whatsapp && (
                <div className="flex gap-3">
                  <MessageCircle className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-mono uppercase text-neutral-500">WhatsApp</div>
                    {waLink ? (
                      <a href={waLink} target="_blank" rel="noreferrer" className="hover:underline font-medium">
                        {settings.company_whatsapp}
                      </a>
                    ) : (
                      <span>{settings.company_whatsapp}</span>
                    )}
                  </div>
                </div>
              )}
              {settings.company_phone && (
                <div className="flex gap-3">
                  <Phone className="w-4 h-4 mt-0.5 text-[#FF3B30] flex-shrink-0" />
                  <div>
                    <div className="text-xs font-mono uppercase text-neutral-500">Teléfono</div>
                    <a href={`tel:${settings.company_phone}`} className="hover:underline">{settings.company_phone}</a>
                  </div>
                </div>
              )}
              {settings.company_email && (
                <div className="flex gap-3">
                  <Mail className="w-4 h-4 mt-0.5 text-[#FF3B30] flex-shrink-0" />
                  <div>
                    <div className="text-xs font-mono uppercase text-neutral-500">Correo</div>
                    <a href={`mailto:${settings.company_email}`} className="hover:underline">{settings.company_email}</a>
                  </div>
                </div>
              )}
              {settings.company_hours && (
                <div className="flex gap-3">
                  <Clock className="w-4 h-4 mt-0.5 text-[#FF3B30] flex-shrink-0" />
                  <div>
                    <div className="text-xs font-mono uppercase text-neutral-500">Horario</div>
                    <div>{settings.company_hours}</div>
                  </div>
                </div>
              )}
              {fullAddress && (
                <div className="flex gap-3" data-testid="contact-address-block">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#FF3B30] flex-shrink-0" />
                  <div>
                    <div className="text-xs font-mono uppercase text-neutral-500">Dirección</div>
                    <div className="whitespace-pre-line leading-snug">{fullAddress}</div>
                    {mapsHref && (
                      <a
                        href={mapsHref}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block mt-1 text-xs font-mono text-[#FF3B30] hover:underline"
                        data-testid="contact-address-maps"
                      >
                        Ver en Google Maps →
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {settings.social_links?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-neutral-200">
                <div className="text-xs font-mono uppercase text-neutral-500 mb-2">Redes</div>
                <div className="flex gap-2 flex-wrap">
                  {settings.social_links.map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-sm px-3 py-1 border border-neutral-200 hover:border-black">
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Business images — up to 2 pictures uploaded from Admin > Settings */}
        <div className="lg:col-span-3">
          {contactImage && contactImage2 ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="brutal-card overflow-hidden aspect-[4/5]" data-testid="contact-image">
                <img
                  src={contactImage}
                  alt={`${settings.company_name || "Nuestro negocio"}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="brutal-card overflow-hidden aspect-[4/5]" data-testid="contact-image-2">
                <img
                  src={contactImage2}
                  alt={`${settings.company_name || "Nuestro negocio"} — 2`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          ) : (
            <div className="brutal-card overflow-hidden aspect-[4/3]" data-testid="contact-image">
              {contactImage || contactImage2 ? (
                <img
                  src={contactImage || contactImage2}
                  alt={`${settings.company_name || "Nuestro negocio"}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-neutral-100 flex flex-col items-center justify-center text-neutral-400 p-6">
                  <div className="w-16 h-16 border-2 border-neutral-300 rounded-full flex items-center justify-center mb-3">
                    <MessageCircle className="w-8 h-8" />
                  </div>
                  <div className="text-xs font-mono uppercase">Imagen del negocio</div>
                  <div className="text-xs mt-2 max-w-xs text-center">
                    Sube hasta 2 fotos del negocio, taller o equipo desde <span className="font-mono">Admin › Ajustes › Imágenes de contacto</span>.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
